import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

// ─── FDA % Daily Value references ────────────────────────────────────────────

const DV = {
  total_fat_g: 78,
  saturated_fat_g: 20,
  cholesterol_mg: 300,
  sodium_mg: 2300,
  total_carbs_g: 275,
  dietary_fiber_g: 28,
  protein_g: 50,
};

function pct(value: number, dv: number): number {
  return Math.round((value / dv) * 100);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NutritionSummary {
  calories: number;
  protein_g: number;
  total_carbs_g: number;
  total_fat_g: number;
}

interface NutritionValues extends NutritionSummary {
  saturated_fat_g: number;
  cholesterol_mg: number;
  sodium_mg: number;
  dietary_fiber_g: number;
  sugars_g: number;
}

interface IngredientNutrition {
  calories: number;
  total_fat_g: number;
  total_carbs_g: number;
  protein_g: number;
  source: "usda" | "claude" | "unmatched";
  ingredientName?: string;
}

interface FullNutritionData {
  perServing: NutritionValues;
  perRecipe: NutritionValues;
  ingredientBreakdown: Record<string, IngredientNutrition>;
  servingsUsed: number | null;
  confidence: string | null;
}

// ─── Helper: stop both bubbling and default link navigation ──────────────────

function stopAll(e: React.MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function LabelRow({
  label,
  value,
  unit,
  bold,
  indent,
  thick,
  dailyPct,
}: {
  label: string;
  value: number;
  unit?: string;
  bold?: boolean;
  indent?: boolean;
  thick?: boolean;
  dailyPct?: number;
}) {
  const valueStr = `${Math.round(value * 10) / 10}${unit ?? ""}`;

  return (
    <div
      className={cn(
        "flex justify-between items-baseline py-0.5",
        thick ? "border-t-4 border-black" : "border-t border-black/20",
        indent && "pl-4"
      )}
    >
      {dailyPct !== undefined ? (
        <>
          <span className={cn("text-xs", bold ? "font-black" : "font-normal")}>
            {label}{" "}
            <span className="font-normal">{valueStr}</span>
          </span>
          <span className="text-xs font-bold">{dailyPct}%</span>
        </>
      ) : (
        <>
          <span className={cn("text-xs", bold ? "font-black" : "font-normal")}>{label}</span>
          <span className={cn("text-xs", bold ? "font-black" : "font-normal")}>{valueStr}</span>
        </>
      )}
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: string | null }) {
  if (confidence === "high") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300 font-medium">
        All ingredients verified by nutrition database
      </span>
    );
  }
  if (confidence === "medium") {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300 font-medium">
        Some values estimated by AI
      </span>
    );
  }
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 font-medium">
      Many values are AI estimates — treat as approximate
    </span>
  );
}

// ─── Ingredient breakdown sub-section ─────────────────────────────────────────

function IngredientBreakdown({
  breakdown,
}: {
  breakdown: Record<string, IngredientNutrition>;
}) {
  const [open, setOpen] = useState(false);

  const sorted = Object.entries(breakdown).sort(
    ([, a], [, b]) => b.calories - a.calories
  );

  if (sorted.length === 0) return null;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={(e) => { stopAll(e); setOpen((v) => !v); }}
        className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Where do the numbers come from?
      </button>
      {open && (
        <div className="mt-2 border rounded overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-2 py-1 font-medium">Ingredient</th>
                <th className="text-right px-2 py-1 font-medium">Cal</th>
                <th className="text-right px-2 py-1 font-medium">Fat</th>
                <th className="text-right px-2 py-1 font-medium">Carbs</th>
                <th className="text-right px-2 py-1 font-medium">Pro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map(([ingId, nutr]) => {
                const name = nutr.ingredientName ?? `Ingredient #${ingId}`;
                return (
                  <tr key={ingId} className="hover:bg-muted/20">
                    <td className="px-2 py-1 max-w-[120px]">
                      <span className="truncate block" title={name}>
                        {name}
                      </span>
                      {nutr.source === "claude" && (
                        <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-700 border border-amber-200">
                          AI estimate
                        </span>
                      )}
                      {nutr.source === "unmatched" && (
                        <span className="text-[9px] px-1 rounded bg-gray-100 text-gray-500 border border-gray-200">
                          ⚠ unmatched
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right">{Math.round(nutr.calories)}</td>
                    <td className="px-2 py-1 text-right">{nutr.total_fat_g}g</td>
                    <td className="px-2 py-1 text-right">{nutr.total_carbs_g}g</td>
                    <td className="px-2 py-1 text-right">{nutr.protein_g}g</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Scaled values helper ─────────────────────────────────────────────────────

function scaleValues(vals: NutritionValues, factor: number): NutritionValues {
  if (factor === 1) return vals;
  return {
    calories: vals.calories * factor,
    total_fat_g: vals.total_fat_g * factor,
    saturated_fat_g: vals.saturated_fat_g * factor,
    cholesterol_mg: vals.cholesterol_mg * factor,
    sodium_mg: vals.sodium_mg * factor,
    total_carbs_g: vals.total_carbs_g * factor,
    dietary_fiber_g: vals.dietary_fiber_g * factor,
    sugars_g: vals.sugars_g * factor,
    protein_g: vals.protein_g * factor,
  };
}

// ─── Main NutritionLabel ──────────────────────────────────────────────────────

interface NutritionLabelProps {
  recipeId: number;
  /** When set and view is "Full Recipe", multiply perRecipe values by this factor */
  scale?: number;
  /** When true, show a % Daily Value column (for display page use) */
  showDailyValues?: boolean;
  /** When true, auto-expands and starts loading on mount (for display page use) */
  defaultExpanded?: boolean;
  /** When true, hide the toggle button (content is controlled externally) */
  hideToggle?: boolean;
}

export function NutritionLabel({
  recipeId,
  scale = 1,
  showDailyValues = false,
  defaultExpanded = false,
  hideToggle = false,
}: NutritionLabelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [view, setView] = useState<"serving" | "recipe">("serving");
  const [fullData, setFullData] = useState<FullNutritionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (defaultExpanded && !fullData && !loading) {
      setLoading(true);
      fetch(`${BASE}/api/nutrition/${recipeId}`)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json() as Promise<{ nutrition: FullNutritionData }>;
        })
        .then((json) => { setFullData(json.nutrition); })
        .catch(() => { setError("Could not load nutrition data."); })
        .finally(() => { setLoading(false); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = async (e: React.MouseEvent) => {
    stopAll(e);

    if (expanded) {
      setExpanded(false);
      return;
    }

    setExpanded(true);
    if (!fullData && !loading) {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE}/api/nutrition/${recipeId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json() as { nutrition: FullNutritionData };
        setFullData(json.nutrition);
      } catch {
        setError("Could not load nutrition data.");
      } finally {
        setLoading(false);
      }
    }
  };

  const effectiveScale = view === "recipe" ? scale : 1;

  const vals = fullData
    ? scaleValues(
        view === "serving" ? fullData.perServing : fullData.perRecipe,
        effectiveScale
      )
    : null;

  const servingsDisplay = fullData?.servingsUsed != null
    ? `${fullData.servingsUsed} servings`
    : "Unknown servings";

  return (
    <div className={hideToggle ? "" : "mt-2"} onClick={stopAll}>
      {!hideToggle && (
        <button
          type="button"
          onClick={handleToggle}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Nutrition Details
        </button>
      )}

      {(expanded || hideToggle) && (
        <div
          className={hideToggle
            ? "space-y-3"
            : "mt-2 bg-white border border-border/60 rounded-xl shadow-sm p-3 space-y-3"
          }
          onClick={stopAll}
        >
          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading nutrition data…
            </div>
          )}
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
          {fullData && vals && (
            <>
              <div className="flex gap-1 border border-border/60 rounded-lg overflow-hidden text-[11px] w-fit">
                <button
                  type="button"
                  onClick={(e) => { stopAll(e); setView("serving"); }}
                  className={cn(
                    "px-2.5 py-1 transition-colors",
                    view === "serving" ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  )}
                >
                  Per Serving
                </button>
                <button
                  type="button"
                  onClick={(e) => { stopAll(e); setView("recipe"); }}
                  className={cn(
                    "px-2.5 py-1 transition-colors border-l border-border/60",
                    view === "recipe" ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                  )}
                >
                  Full Recipe
                </button>
              </div>

              <div
                className="border-2 border-black rounded p-2.5 font-sans"
                style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
              >
                <div className="text-xl font-black leading-none border-b-4 border-black pb-1 mb-1">
                  Nutrition Facts
                </div>
                <div className="text-[10px] border-b border-black/30 pb-1 mb-1">
                  {view === "serving" ? `Serving size 1 of ${servingsDisplay}` : servingsDisplay}
                  {view === "recipe" && scale !== 1 && (
                    <span className="ml-1 text-muted-foreground">(×{scale} scaled)</span>
                  )}
                </div>

                <div className="border-t-8 border-black pt-1">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs font-bold">Calories</span>
                    <span className="text-2xl font-black leading-none">
                      {Math.round(vals.calories)}
                    </span>
                  </div>
                </div>

                <div className="border-t-4 border-black pt-1">
                  {showDailyValues && (
                    <div className="flex justify-end pb-0.5">
                      <span className="text-[9px] font-bold">% Daily Value*</span>
                    </div>
                  )}
                  <LabelRow
                    label="Total Fat"
                    value={vals.total_fat_g}
                    unit="g"
                    bold
                    dailyPct={showDailyValues ? pct(vals.total_fat_g, DV.total_fat_g) : undefined}
                  />
                  <LabelRow
                    label="Saturated Fat"
                    value={vals.saturated_fat_g}
                    unit="g"
                    indent
                    dailyPct={showDailyValues ? pct(vals.saturated_fat_g, DV.saturated_fat_g) : undefined}
                  />
                  <LabelRow
                    label="Cholesterol"
                    value={vals.cholesterol_mg}
                    unit="mg"
                    bold
                    dailyPct={showDailyValues ? pct(vals.cholesterol_mg, DV.cholesterol_mg) : undefined}
                  />
                  <LabelRow
                    label="Sodium"
                    value={vals.sodium_mg}
                    unit="mg"
                    bold
                    dailyPct={showDailyValues ? pct(vals.sodium_mg, DV.sodium_mg) : undefined}
                  />
                  <LabelRow
                    label="Total Carbohydrates"
                    value={vals.total_carbs_g}
                    unit="g"
                    bold
                    dailyPct={showDailyValues ? pct(vals.total_carbs_g, DV.total_carbs_g) : undefined}
                  />
                  <LabelRow
                    label="Dietary Fiber"
                    value={vals.dietary_fiber_g}
                    unit="g"
                    indent
                    dailyPct={showDailyValues ? pct(vals.dietary_fiber_g, DV.dietary_fiber_g) : undefined}
                  />
                  <LabelRow
                    label="Total Sugars"
                    value={vals.sugars_g}
                    unit="g"
                    indent
                  />
                  <LabelRow
                    label="Protein"
                    value={vals.protein_g}
                    unit="g"
                    bold
                    thick
                    dailyPct={showDailyValues ? pct(vals.protein_g, DV.protein_g) : undefined}
                  />
                </div>

                <p className="text-[9px] text-muted-foreground mt-1.5 border-t pt-1">
                  {showDailyValues
                    ? "* Percent Daily Values are based on a 2,000 calorie diet. Values are estimates."
                    : "* Estimated values only."}
                </p>
              </div>

              <ConfidenceBadge confidence={fullData.confidence} />
              <IngredientBreakdown breakdown={fullData.ingredientBreakdown} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
